/**
 * FIG-438: match a purchase-order code to a Variant by value, across identifier
 * types.
 *
 * Before this, a PO code linked only when a Variant carried the same value under
 * the same identifier type, so a code stored as SKU from a sales upload never met
 * the same code read off a PO column headed "PART NUMBER". The rule is now value
 * first: a code links when it equals an identifier on exactly one Variant,
 * whatever type that identifier has.
 *
 * Case is part of the identity. `toMatchKey` trims and normalizes, and keeps
 * letter case, so `Apple-Shop` and `apple-shop` stay different codes.
 *
 * This is a deliberate separate matcher rather than the UC-1/UC-2 observation
 * pipeline: that pipeline matches whenever identifiers arrive, and the rule here
 * is that a PO is matched when it is uploaded and when a mapping CSV is
 * uploaded, and at no other time. A line left unmatched stays unmatched until an
 * admin resolves it.
 */
import { toMatchKey } from "./matchKey.ts";

/** FIG-417: the type a PO code gets when the reader found no column header. */
export const DEFAULT_PO_IDENTIFIER_TYPE = "MPN";

export interface PoCode {
  /** The code as the PO reader read it. */
  value: string;
  /** Printed column header, or MPN when the reader found none (FIG-417). */
  type: string;
}

export interface MatchableLink {
  masterProductId: string;
  identifierValue: string;
  identifierType?: string | null;
  /** false once an admin has unmatched it. */
  active?: boolean | null;
}

/** The identifier a caller should write onto the Variant after linking. */
export interface IdentifierToAdd {
  value: string;
  type: string;
}

export type PoMatchOutcome =
  /** Exactly one Variant carries this value. */
  | { kind: "linked"; masterProductId: string; addIdentifier: IdentifierToAdd | null }
  /** Rule 3: several Variants carry it, so an admin picks (rule 4). */
  | { kind: "ambiguous"; masterProductIds: string[] }
  /** The admin removed this very link before; rule 4 applies instead. */
  | { kind: "withheld"; masterProductId: string }
  /** Rule 4: nothing carries this value. */
  | { kind: "unmatched" };

/** Identifier types are names, so they compare without regard to case. */
const sameType = (left: string | null | undefined, right: string) =>
  String(left ?? "").trim().toLowerCase() === right.trim().toLowerCase();

/**
 * Decides what one PO code should link to. Pure: the caller does the reading and
 * the writing, which keeps the rules testable without a database.
 */
export function matchPoCode(code: PoCode, links: MatchableLink[]): PoMatchOutcome {
  const key = toMatchKey(code.value);
  if (!key) return { kind: "unmatched" };

  const sameValue = links.filter((link) => toMatchKey(link.identifierValue) === key);
  const active = sameValue.filter((link) => link.active !== false);

  const masters: string[] = [];
  for (const link of active) {
    if (!link.masterProductId) continue;
    if (!masters.includes(link.masterProductId)) masters.push(link.masterProductId);
  }

  if (masters.length > 1) return { kind: "ambiguous", masterProductIds: masters.sort() };

  if (masters.length === 0) {
    // A value whose only links were switched off was unmatched on purpose.
    // Re-linking it here would undo that the next time a PO arrives.
    const removed = sameValue.find((link) => link.active === false && !!link.masterProductId);
    if (removed) return { kind: "withheld", masterProductId: removed.masterProductId };
    return { kind: "unmatched" };
  }

  const masterProductId = masters[0];

  // Rule 2: the Variant already carries this value under this type, so the link
  // is all there is to do. Rule 1: it carries the value under another type, so
  // the code is also added under the PO's own type.
  const alreadyTyped = active.some(
    (link) => link.masterProductId === masterProductId && sameType(link.identifierType, code.type),
  );
  // An admin who removed this exact value-and-type from this Variant should not
  // have it written back by the next upload.
  const typeRemoved = sameValue.some(
    (link) =>
      link.active === false
      && link.masterProductId === masterProductId
      && sameType(link.identifierType, code.type),
  );

  return {
    kind: "linked",
    masterProductId,
    addIdentifier: alreadyTyped || typeRemoved ? null : { value: code.value, type: code.type },
  };
}

/** Convenience for a whole PO: the outcome per line, in the order given. */
export function matchPoCodes(codes: PoCode[], links: MatchableLink[]): PoMatchOutcome[] {
  return codes.map((code) => matchPoCode(code, links));
}

export interface LegacyAliasRow {
  alias: string;
  primary: string;
}

/**
 * FIG-438 rule 6: one definition of "matched", used by the Line Items badge and
 * by Variant Mapping alike.
 *
 * A line counts as matched only when it is linked to a Variant. A code that
 * merely appears somewhere in sales or inventory data does not count, and
 * neither does a value sitting on more than one Variant -- that is the
 * ambiguous case an admin still has to resolve.
 *
 * The one indirection allowed is a legacy product_mappings alias: it counts
 * when the alias's primary is on exactly one Variant, and the line is then
 * treated as linked to that Variant. Funding resolves the same way.
 */
export function isPoLineMatched(
  code: PoCode,
  links: MatchableLink[],
  legacyAliases: LegacyAliasRow[] = [],
): boolean {
  if (matchPoCode(code, links).kind === "linked") return true;

  const key = toMatchKey(code.value);
  if (!key) return false;

  // The legacy path is not made case-insensitive here; case stays part of the
  // identity, and rows saved lowercased are repaired rather than matched loosely.
  for (const row of legacyAliases) {
    if (toMatchKey(row.alias) !== key) continue;
    if (matchPoCode({ value: row.primary, type: code.type }, links).kind === "linked") return true;
  }
  return false;
}
